/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import {
  SUPPORTED_FILE_EXTENSIONS,
  SUPPORTED_IMAGE_EXTENSIONS,
  SUPPORTED_VIDEO_EXTENSIONS
} from '~backoffice/app/~shared/constants/global.constants';
import { Resource } from '~backoffice/app/~shared/models/shared.models';
import { BadgeService } from '~core/services/badge.service';
import { SnackbarService } from '~backoffice/app/~shared/services/snackbar.service';
import { ProgramService } from '~core/services/program.service';
import { GlobalFacade } from '~backoffice/app/~store/facades/global.facade';
import { ResourceFacade } from '~backoffice/app/~store/facades/resource.facade';
import { Subject, finalize, map, takeUntil, tap } from 'rxjs';
import { Program, ProgramCreationParams } from '~core/models/program.models';
import { ThematicService } from '~core/services/thematic.service';
import { Thematic } from '~core/models/thematic.models';
import { PersonService } from '~core/services/person.service';
import { GenreService } from '~core/services/genre.service';
import {
  httpErrorToFormGroupError,
  setErrorToControls
} from '@veone/ngx-utilities/form';
import { removeNilProperties } from '~backoffice/app/~shared/utils/utils';

@Component({
  standalone: false,
  selector: 'edit-program',
  templateUrl: './edit-program.component.html',
  styleUrls: ['./edit-program.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditProgramComponent implements OnInit, OnChanges, OnDestroy {
  @Input() program: Program | null | undefined;
  isLoading = false;

  thematics$ = this.thematicService
    .list({ itemPerPage: 1000, published: true })
    .pipe(map(res => res.items));

  badges$ = this.badgeService
    .list({ itemPerPage: 1000, published: true })
    .pipe(map(res => res.items));

  persons$ = this.personService
    .list({ itemPerPage: 1000 })
    .pipe(map(res => res.items));

  genres$ = this.genreService
    .list({ itemPerPage: 1000 })
    .pipe(map(res => res.items));

  form = new FormGroup({
    name: new FormControl<string>('', [
      Validators.required,
      Validators.minLength(3)
    ]),
    ageRating: new FormControl<number | null>(null, [
      Validators.pattern(/^[0-9]\d*$/)
    ]),
    description: new FormControl<string>('', [
      Validators.required,
      Validators.minLength(3)
    ]),
    broadcastPeriodLabel: new FormControl<string>('', [
      Validators.minLength(3)
    ]),
    thematicId: new FormControl<string>('', [Validators.required]),
    // categoryId: new FormControl<string>('', [Validators.required]),
    badgeIds: new FormControl<string[] | null>(null),
    personIds: new FormControl<string[] | null>(null),
    genreIds: new FormControl<string[] | null>(null)
  });

  thumbnailControl = new FormControl<File | null>(null);
  logoControl = new FormControl<File | null>(null);

  fileTabs: Resource[] = [
    { id: '1', name: `Vignette du programme` },
    { id: '2', name: `Logo du programme` }
  ];

  selectedFileTab: Resource = this.fileTabs[0];

  supportedImageExtensions = SUPPORTED_IMAGE_EXTENSIONS;
  supportedFileExtensions = SUPPORTED_FILE_EXTENSIONS;
  supportedVideoExtensions = SUPPORTED_VIDEO_EXTENSIONS;

  violations$ = this.globalFacade.formViolations$.pipe(
    tap({
      next: violations => {
        setTimeout(() => {
          violations?.forEach(violation => {
            if (violation.propertyPath.includes('logo')) {
              this.logoControl.setErrors({
                httpError: violation.title
              });
              return;
            }
            if (violation.propertyPath.includes('thumbnail')) {
              this.thumbnailControl.setErrors({
                httpError: violation.title
              });
              return;
            }
            this.form.get(violation.propertyPath)?.setErrors({
              httpError: violation.title
            });
          });
          this.cdRef.detectChanges();
        }, 500);
      }
    })
  );

  private _destroy$ = new Subject<void>();
  constructor(
    private globalFacade: GlobalFacade,
    private programService: ProgramService,
    private snackbarService: SnackbarService,
    private resourceFacade: ResourceFacade,
    private dialogRef: MatDialogRef<EditProgramComponent>,
    private thematicService: ThematicService,
    private badgeService: BadgeService,
    private personService: PersonService,
    private genreService: GenreService,
    private cdRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.patchFormValues(this.program);
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.patchFormValues(changes['program']?.currentValue);
  }

  ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();
  }

  patchFormValues(program: Program | null | undefined): void {
    if (program) {
      this.form.patchValue({
        thematicId: (program.thematic as Thematic | undefined)?.id,
        // categoryId: (program.category as Category | undefined)?.id,
        name: program.name,
        ageRating: program.ageRating,
        description: program.description,
        broadcastPeriodLabel: program.broadcastPeriodLabel,
        badgeIds: program.badges?.map(item => item.id),
        personIds: program.persons?.map(item => item.id),
        genreIds: program.genres?.map(item => item.id)
      });
    }
  }

  save(): void {
    this.isLoading = true;
    const program = this.program;
    const { badgeIds, ...formValues } = this.form.value;
    const data = {
      ...formValues,
      ageRating: formValues.ageRating || 0,
      badgeIds,
      ...(this.logoControl.value && {
        logo: this.logoControl.value
      }),
      ...(this.thumbnailControl.value && {
        thumbnail: this.thumbnailControl.value
      })
    };

    const dataWithoutEmpty = removeNilProperties(data);

    const request$ = program
      ? this.programService.update(
          program.id,
          dataWithoutEmpty as unknown as ProgramCreationParams
        )
      : this.programService.create(
          dataWithoutEmpty as unknown as ProgramCreationParams
        );

    request$
      .pipe(
        takeUntil(this._destroy$),
        tap({
          next: () => {
            this.dialogRef.close(true);
            this.resourceFacade.getData({ refresh: true });
            this.snackbarService.open({
              text: program ? `Programme mis à jour` : `Programme créée`,
              type: 'success'
            });
          },
          error: error => {
            const formGroupError = httpErrorToFormGroupError(error);
            const logoError = formGroupError.find(el =>
              el.propertyPath.includes('logo')
            );
            if (logoError) {
              this.logoControl.setErrors({
                httpError: logoError.title
              });
            }
            const thumbnailError = formGroupError.find(el =>
              el.propertyPath.includes('thumbnail')
            );
            if (thumbnailError) {
              this.thumbnailControl.setErrors({
                httpError: thumbnailError.title
              });
            }
            setErrorToControls(this.form, formGroupError);
          }
        }),
        finalize(() => {
          this.isLoading = false;
          this.cdRef.detectChanges();
        })
      )
      .subscribe();
  }

  setFile(fileType: 'logo' | 'thumbnail', files: File[] | null): void {
    if (fileType === 'thumbnail') {
      this.thumbnailControl.patchValue(files ? files[0] : null);
      return;
    }
    this.logoControl.patchValue(files ? files[0] : null);
    this.resetFormErrors();
  }

  resetFormErrors(): void {
    const keys = Object.keys(this.form.controls);
    keys.forEach(key => {
      this.form.get(key)?.setErrors(null);
      this.form.get(key)?.updateValueAndValidity();
    });
    this.cdRef.detectChanges();
  }

  get modalTitle(): string {
    return this.program
      ? `Modification du programme`
      : `Configuration du programme`;
  }

  get modalDescription(): string {
    return this.program
      ? `Veuillez éditer les champs ci-dessous pour mettre à jour le programme`
      : `Veuillez remplir les champs ci-dessous pour la création d'un nouveau programme`;
  }

  get isFormValid(): boolean {
    return this.program
      ? this.form.valid
      : this.form.valid &&
          !!this.thumbnailControl.value &&
          !!this.logoControl.value;
  }
}
